import { Erod, Ty } from '@cotera/era';

export type ValidateWorkflowRunInputResult =
  | { readonly valid: true }
  | {
      readonly valid: false;
      readonly message: string;
      readonly nodeId?: string;
    };

export type WorkflowRunInputSchemaMode =
  | { readonly kind: 'empty' }
  | { readonly kind: 'struct' }
  | { readonly kind: 'primitive' };

export type WorkflowRunInputSchemaBundle = {
  readonly schema: Erod.Def<unknown>;
  readonly mode: WorkflowRunInputSchemaMode;
};

type WorkflowRunInputSchemaInternalBundle = WorkflowRunInputSchemaBundle & {
  readonly validationSchema: Erod.Def<unknown>;
};

const FALLBACK_RUN_INPUT_SCHEMA = Erod.Schema.record(
  Erod.Schema.super()
).describe(
  'Workflow run input — must satisfy the workflow entry node input shape.'
);

const EMPTY_RUN_INPUT_SCHEMA = Erod.Schema.object({}).describe(
  'No inputs required for this workflow — pass an empty object.'
);

export function runInputSchemaModeFromTy(
  ty: Ty.ExtendedAttributeType
): WorkflowRunInputSchemaMode {
  if (ty.ty.k === 'struct' && Object.keys(ty.ty.fields).length === 0) {
    return { kind: 'empty' };
  }
  if (ty.ty.k === 'struct') {
    return { kind: 'struct' };
  }
  if (ty.ty.k === 'primitive' && ty.ty.t === 'super') {
    return { kind: 'struct' };
  }
  return { kind: 'primitive' };
}

function isScalarEntryRunInputTy(ty: Ty.ExtendedAttributeType): boolean {
  return ty.ty.k !== 'struct';
}

function schemaFromRunInputTy(
  ty: Ty.ExtendedAttributeType
): WorkflowRunInputSchemaInternalBundle {
  const mode = runInputSchemaModeFromTy(ty);

  if (mode.kind === 'empty') {
    return {
      schema: EMPTY_RUN_INPUT_SCHEMA,
      validationSchema: EMPTY_RUN_INPUT_SCHEMA,
      mode,
    };
  }

  if (
    mode.kind === 'struct' &&
    ty.ty.k === 'primitive' &&
    ty.ty.t === 'super'
  ) {
    return {
      schema: FALLBACK_RUN_INPUT_SCHEMA,
      validationSchema: FALLBACK_RUN_INPUT_SCHEMA,
      mode,
    };
  }

  // LLM tool JSON Schema must be type: "object". Scalar entry shapes (e.g.
  // string) use a { value } wrapper for the tool schema; validate/normalize
  // still operate on the bare scalar the workflow engine expects.
  if (isScalarEntryRunInputTy(ty)) {
    const typeDisplay = Ty.displayTy(ty);
    const bareSchema = Erod.Schema.bareSchemaFromTy(ty).describe(
      `Workflow run input (${typeDisplay}).`
    );
    return {
      schema: Erod.Schema.object({
        value: bareSchema,
      }).describe(
        `Workflow run input (${typeDisplay}). Pass { "value": <${typeDisplay}> }.`
      ),
      validationSchema: bareSchema,
      mode: { kind: 'primitive' },
    };
  }

  if (mode.kind === 'struct') {
    const typeDisplay = Ty.displayTy(ty);
    const structSchema = Erod.Schema.bareSchemaFromTy(ty).describe(
      `Workflow run input object matching entry shape: ${typeDisplay}`
    );
    return {
      schema: structSchema,
      validationSchema: structSchema,
      mode,
    };
  }

  return {
    schema: FALLBACK_RUN_INPUT_SCHEMA,
    validationSchema: FALLBACK_RUN_INPUT_SCHEMA,
    mode: { kind: 'struct' },
  };
}

/** Resolved run-input schema for a workflow definition graph. */
export class WorkflowRunInputSchema {
  /** Schema exposed to LLM tool calling (always an object JSON Schema). */
  readonly schema: Erod.Def<unknown>;
  readonly mode: WorkflowRunInputSchemaMode;
  /** Schema used to validate the run payload the workflow engine expects. */
  private readonly validationSchema: Erod.Def<unknown>;

  private constructor(bundle: WorkflowRunInputSchemaInternalBundle) {
    this.schema = bundle.schema;
    this.mode = bundle.mode;
    this.validationSchema = bundle.validationSchema;
  }

  static fromTy(ty: Ty.ExtendedAttributeType): WorkflowRunInputSchema {
    return new WorkflowRunInputSchema(schemaFromRunInputTy(ty));
  }

  validate(runInput: unknown): ValidateWorkflowRunInputResult {
    const result = this.validationSchema
      .zodSchema({ strict: true })
      .safeParse(runInput);
    if (!result.success) {
      if (
        this.mode.kind === 'primitive' &&
        runInput !== null &&
        typeof runInput === 'object' &&
        !Array.isArray(runInput)
      ) {
        return {
          valid: false,
          message: `${result.error.message}. For scalar workflow inputs, pass { "value": <scalar> } (or { "prompt": <string> }).`,
        };
      }
      return {
        valid: false,
        message: result.error.message,
      };
    }
    return { valid: true };
  }

  /**
   * Maps tool-call JSON to the run input payload the workflow engine expects.
   * For primitive entry shapes, unwraps `{ value: ... }` wrappers from the LLM.
   */
  normalizeToolInput(input: unknown): unknown {
    return normalizeWorkflowToolInputToRunInput(this.mode, input);
  }

  toBundle(): WorkflowRunInputSchemaBundle {
    return {
      schema: this.schema,
      mode: this.mode,
    };
  }
}

/** Maps tool-call JSON to the run input payload the workflow engine expects. */
export function normalizeWorkflowToolInputToRunInput(
  mode: WorkflowRunInputSchemaMode,
  input: unknown
): unknown {
  if (mode.kind !== 'primitive') {
    return input;
  }

  // LLMs often pass {} for "no input" workflows that still have a scalar shape.
  if (isPlainObject(input) && Object.keys(input).length === 0) {
    return '';
  }

  let current = input;
  while (isRedundantPrimitiveValueWrapper(current)) {
    current = (current as Record<string, unknown>)[
      getPrimitiveWrapperKey(current as Record<string, unknown>)!
    ];
  }
  return current;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function getPrimitiveWrapperKey(
  value: Record<string, unknown>
): 'value' | 'prompt' | null {
  const keys = Object.keys(value);
  if (keys.length !== 1) {
    return null;
  }
  const key = keys[0];
  if (key === 'value' || key === 'prompt') {
    return key;
  }
  return null;
}

function isRedundantPrimitiveValueWrapper(
  value: unknown
): value is Record<string, unknown> {
  if (!isPlainObject(value)) {
    return false;
  }
  return getPrimitiveWrapperKey(value) !== null;
}
